import React, { useState, useEffect, useMemo } from "react";
import api from "../api";
import {
  HiOutlineBolt,
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
  HiOutlineClipboardDocumentList,
  HiOutlineCalendarDays,
  HiOutlineChartBar,
  HiOutlineArrowTrendingUp,
  HiOutlineClock,
  HiOutlineChatBubbleLeftRight
} from "react-icons/hi2";

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#D1FAE5",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626",
  info: "#0EA5E9"
};

function formatLocalDateISO(dateObj) {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseServerDateToLocal(dateValue) {
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function ProductionEntry({ user, setPage }) {
  const DAILY_TARGET = 40;

  const [leftTab, setLeftTab] = useState("entry");
  const [rightTab, setRightTab] = useState("today");

  const [practice, setPractice] = useState("");
  const [task, setTask] = useState("");
  const [achieved, setAchieved] = useState("");
  const [hours, setHours] = useState("");
  const [date, setDate] = useState(formatLocalDateISO(new Date()));

  const [practices, setPractices] = useState([]);
  const [tasks, setTasks] = useState([]);

  const [todayRecords, setTodayRecords] = useState([]);
  const [monthRecords, setMonthRecords] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadMasters();
    loadProduction();
  }, []);

  const loadMasters = async () => {
    try {
      const [p, t] = await Promise.all([
        api.get("/practices"),
        api.get("/tasks")
      ]);

      setPractices(p.data || []);
      setTasks(t.data || []);
    } catch (err) {
      console.error("Failed to load master data:", err);
      setMessage("❌ Failed to load dropdown data");
    }
  };

  const loadProduction = async () => {
    try {
      const res = await api.get(`/production/user/${user.id}`);
      const allRecords = res.data || [];

      const todayLocalISO = formatLocalDateISO(new Date());
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const todayData = allRecords.filter((r) => {
        const localDate = parseServerDateToLocal(r.date);
        if (!localDate) return false;
        return formatLocalDateISO(localDate) === todayLocalISO;
      });

      const monthData = allRecords.filter((r) => {
        const localDate = parseServerDateToLocal(r.date);
        if (!localDate) return false;
        return (
          localDate.getMonth() === currentMonth &&
          localDate.getFullYear() === currentYear
        );
      });

      setTodayRecords(todayData);
      setMonthRecords(monthData);
    } catch (err) {
      console.error("Failed to load production:", err);
      setMessage("❌ Failed to load production data");
    }
  };

  const filteredTasks = useMemo(() => {
    const selectedPractice = String(practice || "").trim().toLowerCase();

    if (!selectedPractice) return [];

    return (tasks || [])
      .filter(
        (t) =>
          String(t.practice || "").trim().toLowerCase() === selectedPractice
      )
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [tasks, practice]);

  const submit = async () => {
    setMessage("");

    const practiceValue = String(practice || "").trim();
    const taskValue = String(task || "").trim();
    const achievedValue = String(achieved || "").trim();
    const hoursValue = String(hours || "").trim();
    const dateValue = String(date || "").trim();

    if (!practiceValue || !taskValue || !achievedValue || !hoursValue || !dateValue) {
      setMessage("⚠ Please fill all fields");
      return;
    }

    const achievedNumber = Number(achievedValue);
    const hoursNumber = Number(hoursValue);

    if (!Number.isFinite(achievedNumber) || achievedNumber <= 0) {
      setMessage("⚠ Achieved count must be greater than 0");
      return;
    }

    if (!Number.isFinite(hoursNumber) || hoursNumber <= 0) {
      setMessage("⚠ Hours must be greater than 0");
      return;
    }

    try {
      await api.post("/production", {
        user_id: user.id,
        practice: practiceValue,
        task: taskValue,
        production_count: achievedNumber,
        hours_contributed: hoursNumber,
        date: dateValue,
        role: user.role
      });

      setMessage("✅ Production saved");
      setTask("");
      setAchieved("");
      setHours("");

      await loadProduction();
      window.dispatchEvent(new Event("productionUpdated"));
    } catch (err) {
      console.error("Save failed:", err);

      if (err.response?.status === 403) {
        setMessage("⛔ Entry locked after 48 hours. Please raise request");
        return;
      }

      setMessage(err.response?.data?.error || "❌ Something went wrong");
    }
  };

  const resetForm = () => {
    setPractice("");
    setTask("");
    setAchieved("");
    setHours("");
    setDate(formatLocalDateISO(new Date()));
    setMessage("");
  };

  const todayTotal = todayRecords.reduce(
    (sum, r) => sum + Number(r.production_count || 0),
    0
  );

  const progressPercent = Math.min(
    Math.round((todayTotal / DAILY_TARGET) * 100),
    100
  );

  const latestMonth = [...monthRecords]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  const monthWithPercent = monthRecords.map((r) => {
    const target = Number(r.target || DAILY_TARGET);
    const percent = target
      ? ((Number(r.production_count || 0) / target) * 100).toFixed(1)
      : "0.0";

    return {
      ...r,
      target,
      percent
    };
  });

  const totalAchieved = monthWithPercent.reduce(
    (sum, r) => sum + Number(r.production_count || 0),
    0
  );

  const totalHours = monthWithPercent.reduce(
    (sum, r) => sum + Number(r.hours_contributed || 0),
    0
  );

  const totalTarget = monthWithPercent.reduce(
    (sum, r) => sum + Number(r.target || DAILY_TARGET),
    0
  );

  const avgPercent = monthWithPercent.length
    ? (
        monthWithPercent.reduce((sum, r) => sum + Number(r.percent || 0), 0) /
        monthWithPercent.length
      ).toFixed(1)
    : "0.0";

  const achievedPreview = Number(achieved || 0);
  const afterSubmitTotal = todayTotal + achievedPreview;
  const afterSubmitPercent = Math.min(
    Math.round((afterSubmitTotal / DAILY_TARGET) * 100),
    999
  );

  const remainingToTarget = Math.max(DAILY_TARGET - todayTotal, 0);
  const projectedRemaining = Math.max(DAILY_TARGET - afterSubmitTotal, 0);
  const exceedBy = afterSubmitTotal > DAILY_TARGET ? afterSubmitTotal - DAILY_TARGET : 0;
  const entriesCountToday = todayRecords.length;

  const topTaskToday = useMemo(() => {
    const taskMap = {};
    todayRecords.forEach((r) => {
      taskMap[r.task] = (taskMap[r.task] || 0) + Number(r.production_count || 0);
    });

    const keys = Object.keys(taskMap);
    if (!keys.length) return "-";

    return keys.reduce((a, b) => (taskMap[a] > taskMap[b] ? a : b));
  }, [todayRecords]);

  const liveStatus = useMemo(() => {
    if (afterSubmitPercent >= 100) {
      return {
        text: "On Target",
        color: theme.success,
        bg: "#ECFDF5",
        icon: <HiOutlineCheckCircle />
      };
    }

    if (afterSubmitPercent >= 70) {
      return {
        text: "In Progress",
        color: theme.warning,
        bg: "#FFFBEB",
        icon: <HiOutlineBolt />
      };
    }

    return {
      text: "Needs Attention",
      color: theme.danger,
      bg: "#FEF2F2",
      icon: <HiOutlineExclamationTriangle />
    };
  }, [afterSubmitPercent]);

  const liveBarColor =
    afterSubmitPercent >= 100
      ? theme.success
      : afterSubmitPercent >= 70
      ? theme.warning
      : theme.danger;

  return (
    <div style={page}>
      <div style={topHeader}>
        <div>
          <h2 style={pageTitle}>Production Entry</h2>
          <p style={pageSubtitle}>
            Log work, preview impact instantly, and track your daily and monthly output.
          </p>
        </div>
      </div>

      <div style={layout}>
        <div style={mainCard}>
          <div style={tabRow}>
            <button
              onClick={() => setLeftTab("entry")}
              style={{ ...tabBtn, ...(leftTab === "entry" ? activeTabBtn : {}) }}
            >
              Entry Form
            </button>

            <button
              onClick={() => setLeftTab("snapshot")}
              style={{ ...tabBtn, ...(leftTab === "snapshot" ? activeTabBtn : {}) }}
            >
              Today's Snapshot
            </button>
          </div>

          <div style={cardContent}>
            {leftTab === "entry" && (
              <div>
                <div style={sectionTitleRow}>
                  <div style={sectionIcon}>
                    <HiOutlineClipboardDocumentList />
                  </div>
                  <div>
                    <h3 style={sectionTitle}>Entry Form</h3>
                    <p style={sectionSub}>Submit production for a selected date and task.</p>
                  </div>
                </div>

                <label style={label}>Practice</label>
                <select
                  value={practice}
                  onChange={(e) => {
                    const selectedPractice = e.target.value;
                    setPractice(selectedPractice);
                    setTask("");
                  }}
                  style={input}
                >
                  <option value="">Select Practice</option>
                  {practices.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </select>

                <label style={label}>Task</label>
                <select
                  value={task}
                  onChange={(e) => setTask(e.target.value)}
                  style={{
                    ...input,
                    background: practice ? "#FFFFFF" : "#F8FAFC",
                    color: practice ? theme.text : theme.textMuted
                  }}
                  disabled={!practice}
                >
                  <option value="">
                    {practice ? "Select Task" : "Select Practice First"}
                  </option>
                  {filteredTasks.map((t) => (
                    <option key={t.id} value={t.name}>
                      {t.name}
                    </option>
                  ))}
                </select>

                <label style={label}>Achieved</label>
                <input
                  type="text"
                  value={achieved}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) {
                      setAchieved(val);
                    }
                  }}
                  style={input}
                  placeholder="Enter achieved count"
                />

                <label style={label}>Hours</label>
                <input
                  type="text"
                  value={hours}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*\.?\d*$/.test(val)) {
                      setHours(val);
                    }
                  }}
                  style={input}
                  placeholder="Enter hours"
                />

                <label style={label}>Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={input}
                />

                <div style={buttonRow}>
                  <button onClick={submit} style={submitBtn}>
                    Submit
                  </button>
                  <button onClick={resetForm} style={resetBtn}>
                    Reset
                  </button>
                </div>

                {message && (
                  <div
                    style={{
                      ...messageBox,
                      background:
                        message.includes("❌") || message.includes("⚠") || message.includes("⛔")
                          ? "#FEF2F2"
                          : "#ECFDF5",
                      color:
                        message.includes("❌") || message.includes("⚠") || message.includes("⛔")
                          ? theme.danger
                          : theme.success,
                      borderColor:
                        message.includes("❌") || message.includes("⚠") || message.includes("⛔")
                          ? "#FECACA"
                          : "#BBF7D0"
                    }}
                  >
                    {message}
                  </div>
                )}
              </div>
            )}

            {leftTab === "snapshot" && (
              <div style={contentStack}>
                <div style={innerPanel}>
                  <div style={miniHeader}>
                    <HiOutlineArrowTrendingUp style={miniIcon} />
                    <span style={miniTitle}>Today Snapshot</span>
                  </div>

                  <div style={snapshotGrid}>
                    <div style={snapshotStat}>
                      <span style={snapshotLabel}>Entries</span>
                      <span style={snapshotValue}>{entriesCountToday}</span>
                    </div>

                    <div style={snapshotStat}>
                      <span style={snapshotLabel}>Top Task</span>
                      <span style={snapshotValueSmall}>{topTaskToday}</span>
                    </div>
                  </div>
                </div>

                <div style={innerPanel}>
                  <div style={miniHeader}>
                    <HiOutlineBolt style={miniIcon} />
                    <span style={miniTitle}>Remaining to Target</span>
                  </div>

                  <div style={remainingBox}>
                    <div style={remainingRow}>
                      <span style={snapshotLabel}>Current Remaining</span>
                      <span style={remainingValue}>{remainingToTarget}</span>
                    </div>

                    <div style={remainingRow}>
                      <span style={snapshotLabel}>After Submit</span>
                      <span style={remainingValue}>
                        {projectedRemaining === 0 && exceedBy > 0
                          ? `+${exceedBy}`
                          : projectedRemaining}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={innerPanel}>
                  <div style={miniHeader}>
                    <HiOutlineClock style={miniIcon} />
                    <span style={miniTitle}>Late Entry Help</span>
                  </div>

                  <p style={helperText}>
                    Entries older than 48 hours need manager approval through Requests.
                  </p>

                  <button
                    style={linkBtn}
                    onClick={() => {
                      if (setPage) setPage("requests");
                    }}
                  >
                    <HiOutlineChatBubbleLeftRight style={{ fontSize: 16 }} />
                    Raise Request
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={mainCard}>
          <div style={tabRow}>
            <button
              onClick={() => setRightTab("today")}
              style={{ ...tabBtn, ...(rightTab === "today" ? activeTabBtn : {}) }}
            >
              Today
            </button>

            <button
              onClick={() => setRightTab("month")}
              style={{ ...tabBtn, ...(rightTab === "month" ? activeTabBtn : {}) }}
            >
              Month
            </button>
          </div>

          {rightTab === "today" && (
            <div style={contentStack}>
              <div style={highlightPanel}>
                <div style={sectionTitleRow}>
                  <div style={sectionIcon}>
                    <HiOutlineChartBar />
                  </div>
                  <div>
                    <h3 style={sectionTitle}>Live Progress Preview</h3>
                    <p style={sectionSub}>See the effect of this entry before you submit it.</p>
                  </div>
                </div>

                <div style={previewGrid}>
                  <div style={previewStat}>
                    <span style={previewLabel}>Current Total</span>
                    <span style={previewValue}>{todayTotal}</span>
                  </div>

                  <div style={previewStat}>
                    <span style={previewLabel}>After Submit</span>
                    <span style={previewValue}>{afterSubmitTotal}</span>
                  </div>

                  <div style={previewStat}>
                    <span style={previewLabel}>Current %</span>
                    <span style={previewValue}>{progressPercent}%</span>
                  </div>

                  <div style={previewStat}>
                    <span style={previewLabel}>After Submit %</span>
                    <span style={previewValue}>{afterSubmitPercent}%</span>
                  </div>
                </div>

                <div style={{ marginTop: 18 }}>
                  <div style={progressLabelRow}>
                    <span style={progressLabel}>Projected Progress</span>
                    <span style={progressLabel}>{afterSubmitPercent}%</span>
                  </div>

                  <div style={progressBg}>
                    <div
                      style={{
                        ...progressBar,
                        width: `${Math.min(afterSubmitPercent, 100)}%`,
                        background: liveBarColor
                      }}
                    />
                  </div>

                  <div
                    style={{
                      ...statusPill,
                      background: liveStatus.bg,
                      color: liveStatus.color
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{liveStatus.icon}</span>
                    {liveStatus.text}
                  </div>
                </div>
              </div>

              <div>
                <div style={sectionTitleRow}>
                  <div style={sectionIcon}>
                    <HiOutlineBolt />
                  </div>
                  <div>
                    <h3 style={sectionTitle}>Today's Entries</h3>
                    <p style={sectionSub}>Your logged entries for today.</p>
                  </div>
                </div>

                <table style={table}>
                  <thead>
                    <tr style={tableHeadRow}>
                      <th style={th}>Task</th>
                      <th style={th}>Achieved</th>
                      <th style={th}>Hours</th>
                    </tr>
                  </thead>

                  <tbody>
                    {todayRecords.length === 0 ? (
                      <tr>
                        <td colSpan="3" style={{ ...td, textAlign: "center", color: theme.textMuted }}>
                          No entries for today
                        </td>
                      </tr>
                    ) : (
                      todayRecords.map((r, index) => (
                        <tr
                          key={r.id}
                          style={{
                            background: index % 2 === 0 ? "#FFFFFF" : "#F8FAFC"
                          }}
                        >
                          <td style={td}>{r.task}</td>
                          <td style={td}>{r.production_count}</td>
                          <td style={td}>{r.hours_contributed || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                <div style={summaryRow}>
                  <div style={summaryBox}>
                    <span style={summaryLabel}>Today's Total</span>
                    <span style={summaryValue}>{todayTotal}</span>
                  </div>

                  <div style={summaryBox}>
                    <span style={summaryLabel}>Current Progress</span>
                    <span style={summaryValue}>{progressPercent}%</span>
                  </div>
                </div>

                <div style={{ marginTop: 14 }}>
                  <div style={progressBg}>
                    <div
                      style={{
                        ...progressBar,
                        width: `${Math.min(progressPercent, 100)}%`,
                        background:
                          progressPercent >= 100
                            ? theme.success
                            : progressPercent >= 70
                            ? theme.warning
                            : theme.primary
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {rightTab === "month" && (
            <div>
              <div style={sectionTitleRow}>
                <div style={sectionIcon}>
                  <HiOutlineCalendarDays />
                </div>
                <div>
                  <h3 style={sectionTitle}>Current Month Entries</h3>
                  <p style={sectionSub}>Latest entries and performance summary.</p>
                </div>
              </div>

              <table style={{ ...table, fontSize: "13px" }}>
                <thead>
                  <tr style={tableHeadRow}>
                    <th style={th}>Date</th>
                    <th style={th}>Task</th>
                    <th style={th}>Achieved</th>
                    <th style={th}>Hours</th>
                    <th style={th}>Target</th>
                    <th style={th}>Production %</th>
                  </tr>
                </thead>

                <tbody>
                  {latestMonth.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ ...td, textAlign: "center", color: theme.textMuted }}>
                        No entries this month
                      </td>
                    </tr>
                  ) : (
                    latestMonth.map((r, index) => {
                      const target = Number(r.target || DAILY_TARGET);
                      const percent = target
                        ? ((Number(r.production_count || 0) / target) * 100).toFixed(1)
                        : "0.0";

                      return (
                        <tr
                          key={r.id}
                          style={{
                            background: index % 2 === 0 ? "#FFFFFF" : "#F8FAFC"
                          }}
                        >
                          <td style={td}>{new Date(r.date).toLocaleDateString()}</td>
                          <td style={td}>{r.task}</td>
                          <td style={td}>{r.production_count}</td>
                          <td style={td}>{r.hours_contributed || "-"}</td>
                          <td style={td}>{target}</td>
                          <td style={td}>{percent}%</td>
                        </tr>
                      );
                    })
                  )}

                  <tr style={{ fontWeight: "bold", background: "#ECFEFF" }}>
                    <td colSpan="2" style={td}>Total</td>
                    <td style={td}>{totalAchieved}</td>
                    <td style={td}>{totalHours}</td>
                    <td style={td}>{totalTarget}</td>
                    <td style={td}>{avgPercent}%</td>
                  </tr>
                </tbody>
              </table>

              <button
                onClick={() => setShowModal(true)}
                style={{ ...submitBtn, marginTop: "18px", width: "fit-content" }}
              >
                View Full Month
              </button>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div style={overlay}>
          <div style={modal}>
            <div style={modalHeader}>
              <h3 style={{ margin: 0, color: theme.text }}>Current Month Production</h3>
              <button onClick={() => setShowModal(false)} style={closeBtn}>
                Close
              </button>
            </div>

            <table style={{ ...table, marginTop: "15px" }}>
              <thead>
                <tr style={tableHeadRow}>
                  <th style={th}>Date</th>
                  <th style={th}>Practice</th>
                  <th style={th}>Task</th>
                  <th style={th}>Achieved</th>
                  <th style={th}>Hours</th>
                  <th style={th}>Target</th>
                  <th style={th}>Production %</th>
                </tr>
              </thead>

              <tbody>
                {monthRecords.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ ...td, textAlign: "center", color: theme.textMuted }}>
                      No month data found
                    </td>
                  </tr>
                ) : (
                  monthRecords.map((r, index) => {
                    const target = Number(r.target || DAILY_TARGET);
                    const percent = target
                      ? ((Number(r.production_count || 0) / target) * 100).toFixed(1)
                      : "0.0";

                    return (
                      <tr
                        key={r.id}
                        style={{
                          background: index % 2 === 0 ? "#FFFFFF" : "#F8FAFC"
                        }}
                      >
                        <td style={td}>{new Date(r.date).toLocaleDateString()}</td>
                        <td style={td}>{r.practice}</td>
                        <td style={td}>{r.task}</td>
                        <td style={td}>{r.production_count}</td>
                        <td style={td}>{r.hours_contributed || "-"}</td>
                        <td style={td}>{target}</td>
                        <td style={td}>{percent}%</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= STYLES ================= */

const page = {
  fontFamily: "sans-serif"
};

const topHeader = {
  marginBottom: "22px"
};

const pageTitle = {
  margin: 0,
  fontSize: "28px",
  fontWeight: 700,
  color: theme.text
};

const pageSubtitle = {
  margin: "8px 0 0 0",
  color: theme.textMuted,
  fontSize: "14px"
};

const layout = {
  display: "grid",
  gridTemplateColumns: "420px minmax(0, 1fr)",
  gap: "24px",
  alignItems: "stretch"
};

const mainCard = {
  background: theme.surface,
  padding: "24px",
  borderRadius: "20px",
  boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
  border: "1px solid #F1F5F9",
  minWidth: 0,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box"
};

const cardContent = {
  display: "flex",
  flexDirection: "column",
  flex: 1
};

const tabRow = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "14px",
  marginBottom: "22px"
};

const tabBtn = {
  border: "1px solid #D7DEE7",
  background: "#F8FAFC",
  color: theme.text,
  padding: "14px 16px",
  borderRadius: "16px",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: "14px",
  minHeight: "54px"
};

const activeTabBtn = {
  background: theme.surfaceSoft,
  color: theme.primaryDark,
  border: `1px solid ${theme.border}`
};

const contentStack = {
  display: "flex",
  flexDirection: "column",
  gap: "18px"
};

const highlightPanel = {
  padding: "22px",
  borderRadius: "18px",
  background: "linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)",
  border: `1px solid ${theme.border}`
};

const innerPanel = {
  background: "#F8FAFC",
  border: "1px solid #E2E8F0",
  borderRadius: "14px",
  padding: "14px"
};

const sectionTitleRow = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "18px"
};

const sectionIcon = {
  width: "42px",
  height: "42px",
  borderRadius: "12px",
  background: theme.surfaceSoft,
  color: theme.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px",
  flexShrink: 0
};

const sectionTitle = {
  margin: 0,
  fontSize: "20px",
  fontWeight: 700,
  color: theme.text
};

const sectionSub = {
  margin: "4px 0 0 0",
  fontSize: "13px",
  color: theme.textMuted
};

const label = {
  display: "block",
  marginBottom: "6px",
  marginTop: "14px",
  color: theme.text,
  fontWeight: 600,
  fontSize: "14px"
};

const input = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: "12px",
  border: "1px solid #D1D5DB",
  fontSize: "14px",
  boxSizing: "border-box",
  outline: "none",
  background: "#FFFFFF"
};

const buttonRow = {
  display: "flex",
  gap: "10px",
  marginTop: "18px"
};

const submitBtn = {
  background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
  color: "#fff",
  border: "none",
  padding: "11px 18px",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: 600,
  boxShadow: "0 8px 18px rgba(15,118,110,0.18)"
};

const resetBtn = {
  background: "#E2E8F0",
  color: theme.text,
  border: "none",
  padding: "11px 18px",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: 600
};

const messageBox = {
  marginTop: "14px",
  padding: "10px 12px",
  borderRadius: "12px",
  border: "1px solid",
  fontWeight: 600,
  fontSize: "14px"
};

const miniHeader = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "10px"
};

const miniIcon = {
  fontSize: "18px",
  color: theme.primary
};

const miniTitle = {
  fontSize: "14px",
  fontWeight: 700,
  color: theme.text
};

const snapshotGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "10px"
};

const snapshotStat = {
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "12px",
  padding: "12px",
  minWidth: 0
};

const snapshotLabel = {
  display: "block",
  fontSize: "12px",
  color: theme.textMuted,
  fontWeight: 600,
  marginBottom: "6px"
};

const snapshotValue = {
  fontSize: "24px",
  fontWeight: 800,
  color: theme.text
};

const snapshotValueSmall = {
  fontSize: "14px",
  fontWeight: 700,
  color: theme.text,
  wordBreak: "break-word"
};

const remainingBox = {
  display: "flex",
  flexDirection: "column",
  gap: "10px"
};

const remainingRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "10px"
};

const remainingValue = {
  fontSize: "20px",
  fontWeight: 800,
  color: theme.text
};

const helperText = {
  margin: "0 0 12px 0",
  fontSize: "13px",
  color: theme.textMuted,
  lineHeight: 1.5
};

const linkBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  border: "none",
  background: theme.surfaceSoft,
  color: theme.primaryDark,
  padding: "10px 12px",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: 700
};

const previewGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "14px"
};

const previewStat = {
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "14px",
  padding: "14px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  minWidth: 0
};

const previewLabel = {
  fontSize: "12px",
  color: theme.textMuted,
  fontWeight: 600
};

const previewValue = {
  fontSize: "24px",
  color: theme.text,
  fontWeight: 800
};

const progressLabelRow = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "8px"
};

const progressLabel = {
  fontSize: "13px",
  color: theme.textMuted,
  fontWeight: 600
};

const progressBg = {
  width: "100%",
  height: "14px",
  background: "#E5E7EB",
  borderRadius: "999px",
  overflow: "hidden"
};

const progressBar = {
  height: "14px",
  borderRadius: "999px"
};

const statusPill = {
  marginTop: "16px",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 12px",
  borderRadius: "999px",
  fontWeight: 700,
  fontSize: "13px"
};

const table = {
  width: "100%",
  marginTop: "8px",
  borderCollapse: "separate",
  borderSpacing: 0,
  tableLayout: "fixed"
};

const tableHeadRow = {
  background: "#F1F5F9"
};

const th = {
  padding: "12px 14px",
  textAlign: "left",
  borderBottom: "1px solid #E2E8F0",
  color: theme.text,
  fontWeight: 700,
  whiteSpace: "nowrap"
};

const td = {
  padding: "12px 14px",
  borderBottom: "1px solid #F1F5F9",
  color: theme.text,
  overflowWrap: "break-word"
};

const summaryRow = {
  marginTop: "16px",
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "12px"
};

const summaryBox = {
  background: "#F8FAFC",
  border: "1px solid #E2E8F0",
  borderRadius: "14px",
  padding: "14px",
  display: "flex",
  flexDirection: "column",
  gap: "6px"
};

const summaryLabel = {
  fontSize: "12px",
  color: theme.textMuted,
  fontWeight: 600
};

const summaryValue = {
  fontSize: "24px",
  fontWeight: 800,
  color: theme.text
};

const overlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(15,23,42,0.45)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 2000,
  padding: "20px"
};

const modal = {
  background: "#fff",
  padding: "24px",
  borderRadius: "20px",
  width: "1000px",
  maxHeight: "85vh",
  overflow: "auto",
  boxShadow: "0 20px 50px rgba(15,23,42,0.22)"
};

const modalHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "8px"
};

const closeBtn = {
  background: "#E2E8F0",
  color: theme.text,
  border: "none",
  padding: "10px 14px",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: 600
};

export default ProductionEntry;